'use client'

import { useState } from 'react'
import type { FunctionComponent } from 'react'
import styles from './SelectRegion.module.sass'
import type { ContemberCurrency } from '../utilities/formatAmount'
import { VAKOVAKO_COUNTRY_COOKIE_KEY } from '../server/config'
import { useRouter } from 'next/router'

export type Region = {
	id: string
	name: string
	code: string
	currency?: ContemberCurrency
}

type SelectRegionProps = {
	regions: Region[]
	selectedRegion: Region
	setSelectedRegion: (region: Region) => void
	/**
	 * When true the dropdown menu will open upwards instead of downwards.
	 * Useful when the selector is located close to the bottom edge of the viewport (e.g. in the footer).
	 */
	openUpwards?: boolean
}

export const SelectRegion: FunctionComponent<SelectRegionProps> = ({ regions, selectedRegion, setSelectedRegion, openUpwards = false }) => {
	const [isOpen, setIsOpen] = useState(false)
	const router = useRouter()

	if (!regions || regions.length <= 1) {
		return null
	}

	const currentRegion = regions.find(r => r.code === selectedRegion.code) || regions[0]

	const handleRegionChange = (region: Region) => {
		// Set cookie with expiration date one year from now
		const expirationDate = new Date()
		expirationDate.setFullYear(expirationDate.getFullYear() + 1)

		// Set the cookie using the native document.cookie API
		document.cookie = `${VAKOVAKO_COUNTRY_COOKIE_KEY}=${region.code};path=/;expires=${expirationDate.toUTCString()};SameSite=Strict`

		// Don't update the state here - just reload the page
		// The new region will be picked up from the cookie on the server side
		setIsOpen(false)
		window.location.reload()
	}

	return (
		<div className={styles.regionSelector}>
			<button type="button" className={styles.currentRegion} onClick={() => setIsOpen(!isOpen)}>
				{currentRegion.name}
			</button>

			{isOpen && (
				<div className={openUpwards ? `${styles.dropdown} ${styles.dropdownUp}` : styles.dropdown}>
					{regions
						.slice()
						.sort((a, b) => {
							// If the current region is one of the items being compared, prioritize it
							if (a.code === currentRegion.code) return -1
							if (b.code === currentRegion.code) return 1
							// Then place Česko next if it's not already the current region
							if (a.name === 'Česko' && currentRegion.name !== 'Česko') return -1
							if (b.name === 'Česko' && currentRegion.name !== 'Česko') return 1
							// Sort all other regions alphabetically
							return a.name.localeCompare(b.name)
						})
						.map(region => (
							<button
								key={region.id}
								type="button"
								className={`${styles.regionOption} ${region.code === currentRegion.code ? styles.active : ''}`}
								onClick={() => handleRegionChange(region)}
							>
								{region.name}
							</button>
						))}
				</div>
			)}
		</div>
	)
}
